import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  ScreenerTableColumnType,
  SortingType,
} from '../../types/screener.type';
import './ScreenerTable.css';
import { TableMode } from '../../constants/screener/screener.constant';
import { getCssVariable } from '../../utils/utils';

interface ScreenerTableProps {
  columns: ScreenerTableColumnType[];
  data: any[];
  columnVisibility: { [key: string]: boolean };
  sorting: SortingType;
  setSorting: (sorting: SortingType) => void;
  token?: string;
  isSmallScreen?: boolean;
  isLoading?: boolean;
}

const tableSx = {
  '& thead th': {
    '&:first-of-type::after': {
      content: '""',
      position: 'absolute',
      bottom: '0px',
      left: '24px',
      right: '0px',
      height: '1px',
      backgroundColor: 'var(--text-subheader)',
    },
    '&:not(:first-of-type):not(:last-child)::after': {
      content: '""',
      position: 'absolute',
      bottom: '0px',
      left: '0px',
      right: '0px',
      height: '1px',
      backgroundColor: 'var(--text-subheader)',
    },
    '&:last-child::after': {
      content: '""',
      position: 'absolute',
      bottom: '0px',
      left: '0px',
      right: '24px',
      height: '1px',
      backgroundColor: 'var(--text-subheader)',
    },
  },
};

const ScreenerTable = ({
  columns,
  data,
  columnVisibility,
  sorting,
  setSorting,
  token,
  isSmallScreen,
  isLoading,
}: ScreenerTableProps) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tableContainerRef.current && data.length > 0) {
      tableContainerRef.current.scrollTo({ top: 0 });
    }
  }, [data]);

  const visibleColumns = useMemo(() => {
    return columns.filter((col) => columnVisibility[col.id]);
  }, [columns, columnVisibility]);

  return (
    <TableContainer
      ref={tableContainerRef}
      component={Paper}
      sx={{
        position: 'relative',
        flex: 1,
        overflow: 'auto',
      }}
      elevation={0}
    >
      <Table
        stickyHeader
        sx={{
          borderCollapse: 'collapse',
          // ...isSmallScreen ? tableSx : {},
          ...tableSx,
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              height: '32px',
              verticalAlign: 'top',
              borderBottom: '1px sold #121212',
              '& th:first-of-type': {
                paddingLeft: isSmallScreen ? '24px' : '32px',
                whiteSpace: 'nowrap',
              },
              '& th:last-child': {
                paddingRight: isSmallScreen ? '24px' : '32px',
              },
            }}
          >
            {visibleColumns.map((column) => (
              <TableCell
                key={column.id}
                className={column.headerClassName}
                sx={{
                  padding: '0px',
                  fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
                  fontWeight: '700',
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: getCssVariable('--text-subheader'),
                  borderBottom: 'none',
                }}
              >
                {column.renderHeader
                  ? column.renderHeader(column.id)
                  : column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <div
          style={{
            height: isSmallScreen ? '8px' : '0',
          }}
        ></div>
        <TableBody>
          {data.map((row, index) => (
            <TableRow
              key={row.id || index}
              sx={{
                height: isSmallScreen ? '56px' : '40px',
                '& td:first-of-type': {
                  paddingLeft: '24px',
                },
                '& td:last-child': {
                  paddingRight: '32px',
                },

                '&:nth-of-type(2n)': {
                  backgroundColor: getCssVariable('--row-dark'),
                  ...(isSmallScreen
                    ? {
                        '& td:first-of-type': {
                          backgroundColor: getCssVariable('--row-dark'),
                        },
                      }
                    : {
                        '& td:first-of-type, & td:nth-of-type(2), & td:nth-of-type(3), & td:nth-of-type(4)':
                          {
                            backgroundColor: getCssVariable('--row-dark'),
                          },
                      }),
                },
              }}
            >
              {visibleColumns.map((column) =>
                column.skipCell ? null : (
                  <TableCell
                    key={column.id}
                    colSpan={column.colSpan ?? 1}
                    className={column.cellClassName}
                    sx={{
                      padding: '0px',
                      fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
                      fontSize: '16px',
                      lineHeight: '24px',
                      color: getCssVariable('--text-primary'),
                      verticalAlign: 'middle',
                      borderBottom: 'none',
                    }}
                  >
                    {column.renderCell
                      ? column.renderCell(row, index)
                      : row[column.accessorKey]}
                  </TableCell>
                ),
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ScreenerTable;
